/**
 * NotificationsComponent
 *
 * Description: This component fetches and displays notifications with infinite scroll functionality.
 * It utilizes the `httpRequest` helper for API calls and manages loading state, pagination,
 * and notification rendering, including handling default profile images for users.
 *
 * File Name: NotificationsComponent.tsx
 * Date: 30-09-2024
 *
 * Marker: DOC_START
 */

import React, { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import moment from "moment";
import defaultProfile from "src/assets/images/default_profile_image.png";
import { CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { resetCounter } from "src/Store/reducer";
import { Link } from "react-router-dom";
import NoItemUI from "./NoItemUI";
import NoNotificationIcon from "src/assets/images/not-data/no-notification.png";

interface INotifications {
  _id: string;
  imageUrl: string;
  createdAt: string;
  title: string;
  message: string;
  routeId: string;
  notificationRoute:
    | "addTest"
    | "appoinment"
    | "chat"
    | "discussion"
    | "testAcess"
    | "healthProfile"
    | "accountVerification"
    | "diagnostic-order"
    | "pharma-order"
    | "home-care-order"
    | "my-customer"
    | "paymentandwithdraw"
    | "supportType"
    | "payment-detail"
    | "report"
    | "myProfile";
}
interface INotificationResponse {
  documents: INotifications[];
  paginated: IPaginated;
}

const NotificationsComponent: React.FC = () => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination
  const [allNotifications, setAllNotifications] = useState<INotifications[]>(
    []
  );
  // States for loading status, pagination, and customer data

  useEffect(() => {
    if (containerRef.current) {
      let top = containerRef.current.getBoundingClientRect().top;
      containerRef.current.style.minHeight = `calc(100vh - ${top}px - 20px)`;
      if(allNotifications.length === 0 && !isLoader) {
        containerRef.current.style.display = "flex";
        containerRef.current.style.alignItems = "center";
        containerRef.current.style.justifyContent = "center";
      }else{
        containerRef.current.style.display = "block";
        containerRef.current.style.alignItems = "flex-start";
        containerRef.current.style.justifyContent = "flex-start";
      }
    }
  }, [containerRef.current, allNotifications.length, isLoader]);



  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
  });

  // API_CALL to fetch notifications from the server
  const fetchNotifications = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<INotificationResponse>({
      path: `/notification-setting/notification?${qs.stringify(queryParams)}`,
    });
    if (res) {
      let { documents = [], paginated } = res;
      setAllNotifications((prev) => [...prev, ...documents]);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const handleNavigate = (item: INotifications): string => {
    const { notificationRoute, routeId } = item;
    switch (notificationRoute) {
      case "addTest":
        return `/test-result/${routeId}/view`;
      case "appoinment":
        return `/doctor/appointment/${routeId}/view`;
      case "chat":
        return `/doctor/my-customers/manage/${routeId}?tab=chats`;
      case "discussion":
        return `/discussion/${routeId}/detail`;
      case "testAcess":
        return `/test-result/${routeId}/view`;
      case "healthProfile":
        return `/customer-healthprofile/${routeId}/view`;
      case "accountVerification":
        return `/account-settings`;
      case "diagnostic-order":
        return `/order/${routeId}/View`;
      case "report":
        return `/doctor/my-customers/manage/${routeId}?tab=reports`;
      case "pharma-order":
        return `/pharmacy/order/${routeId}/view`;
      case "home-care-order":
        return `/customer/homecare-appointment/${routeId}/view`;
      case "payment-detail":
        return `/payouts-withdrawal`;
      case "my-customer":
        return `/my-customers`;
      case "paymentandwithdraw":
        return `/payouts-withdrawal?tab=withdrawl`;
      case "supportType":
        return `/support-hub`;
      case "myProfile":
        return `/profile-view`;
      default:
        return "/notifications";
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [queryParams]);

  useEffect(() => {
    (async () => {
      const { res, err } = await httpRequest<any>({
        path: "/notification-setting/read/notification",
      });
      if (res) {
        dispatch(resetCounter("unreadNotificationCount"));
      } else {
        console.log("fetchunReadCount", err);
      }
    })();
  }, []);

  return (
    <div
      className={`parent-box parent-box-padding-issue-resolve`}
      ref={containerRef}
    >
      <InfiniteScroll
        dataLength={allNotifications.length}
        next={() =>
          setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }))
        }
        hasMore={queryParams.page < paginated.totalPages}
        loader={
          isLoader && (
            <div className="text-center">
              <CircularProgress size={20} style={{ color: "#004540" }} />
            </div>
          )
        }
      >
        {allNotifications.length > 0 ? (
          allNotifications.map((item: INotifications, index: number) => (
            <Link
              to={handleNavigate(item)}
              key={index}
              className="parent-box parent-box-shadow-issue p-3 mb-3 mt-3 m-4 block"
            >
              <div className="flex justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="profile-img-container">
                    <img
                      alt="User"
                      className="rounded-full"
                      src={`${item?.imageUrl}`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement; // Assert the type
                        if (target) {
                          target.src = defaultProfile;
                        }
                      }}
                    />
                  </div>
                  <div>
                    <p className="font-size-20px font-Poppins-SemiBold">
                      {item?.title || ""}
                    </p>
                    <p className="font-size-16px font-Poppins-Regular">
                      {item?.message || ""}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="font-size-14px font-Poppins-Regular whitespace-nowrap">
                    {moment(item.createdAt).format("DD-MM-YYYY, hh:mm A")}
                  </p>
                </div>
              </div>
            </Link>
          ))
        ) : !isLoader ? (
          <div className="w-full h-full flex items-center justify-center py-5">
            <NoItemUI
              text="No notifications found"
              text2="You don't have any notifications yet."
              iconImagePath={NoNotificationIcon}
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center py-5 gap-2">
            <CircularProgress size={20} style={{ color: "#004540" }} />
            <p className="font-Poppins-Regular font-size-14px">Loading...</p>
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
};

export default NotificationsComponent;
